import { type WritableDraft } from 'immer';
import { type Observable, of } from 'rxjs';

import { type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';

import { type EngineState } from '../../../+state/engine';
import {
  AbstractAiElementHandler,
  applyFieldSize,
  getElement,
  getInitialProps,
  patchFieldLabel,
  toOptionPickerDefaultValue,
  toSelectionLayout,
  toTileSelectorMode,
  upsertElement,
} from '../../../ai';

import { type RadioContext } from './radio.definition';

export class RadioElementHandler extends AbstractAiElementHandler<'radio'> {
  readonly type = 'radio';

  prepare(context: RadioContext): Observable<RadioContext> {
    this.resolveElementIcon(context.element);
    return of(context);
  }

  process(context: RadioContext, draft: WritableDraft<EngineState>): void {
    let props: PartialPropsOf<'radio'> = getInitialProps<'radio'>(draft, context.element.id, {});
    props = patchFieldLabel<'radio'>(props, context.element);

    if (context.element.isReadOnly !== undefined) {
      props = applyPropsPatch<'radio'>(props, {
        content: { radio: { readonly: context.element.isReadOnly } },
      });
    }

    if (context.element.defaultValue !== undefined) {
      props = applyPropsPatch<'radio'>(props, {
        content: {
          data: { defaultValue: toOptionPickerDefaultValue(context.element.defaultValue) },
        },
      });
    }

    if (context.element.template !== undefined) {
      props = applyPropsPatch<'radio'>(props, {
        content: { mode: toTileSelectorMode(context.element.template) },
      });
    }

    if (context.element.layout !== undefined) {
      props = applyPropsPatch<'radio'>(props, {
        content: { radio: { layout: toSelectionLayout(context.element.layout) } },
      });
    }

    if (context.element.maxColumns !== undefined) {
      props = applyPropsPatch<'radio'>(props, {
        content: { radio: { maxColumns: { mode: 'value', value: context.element.maxColumns } } },
      });
    }

    if (context.element.minColumnWidth !== undefined) {
      props = applyPropsPatch<'radio'>(props, {
        content: { radio: { minWidth: { mode: 'value', value: context.element.minColumnWidth } } },
      });
    }

    if (context.element.displayProperties?.sortBy !== undefined) {
      props = applyPropsPatch<'radio'>(props, {
        content: { sort: { field: context.element.displayProperties.sortBy, direction: 'asc' } },
      });
    }

    const { elementId } = upsertElement(draft, context, props);
    applyFieldSize<'radio'>(getElement(draft, elementId, 'radio'), context.element);
  }
}
